import type { ReactNode } from "react";
import { useContext, useRef } from "react";
import { mergeProps, useFocusRing, useRadio } from "react-aria";
import type { AriaRadioProps } from "react-aria";
import { twMerge } from "tailwind-merge";

import { RadioGroupContext } from "./context/radio-group-context";
import { Divider } from "./Divider";

export interface RadioCardProps extends Omit<AriaRadioProps, "children"> {
  readonly title: string;
  readonly description?: string;
  readonly className?: string;
  readonly children?: ReactNode;
  readonly tag?: ReactNode;
}

export default function RadioCard({
  title,
  description,
  className,
  children,
  tag,
  ...props
}: Readonly<RadioCardProps>) {
  const state = useContext(RadioGroupContext);

  if (!state) throw new Error("RadioCard must be used within a RadioGroup");

  const ref = useRef<HTMLInputElement>(null);
  const { inputProps, isSelected, isDisabled } = useRadio(
    { "aria-label": title, ...props },
    state,
    ref,
  );
  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <div
      className={twMerge(
        "rounded-md border border-border transition-colors",
        isSelected && "bg-secondary",
        isFocusVisible &&
          `ring-primary/50 ring-offset-background ring-2 ring-offset-1`,
        isDisabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <label
        className={twMerge(
          "flex cursor-pointer justify-between gap-4 p-4",
          tag ? "items-start" : "items-center",
        )}
      >
        <input
          ref={ref}
          className="sr-only"
          {...mergeProps(inputProps, focusProps)}
        />

        <div className="flex flex-col gap-4">
          {tag && <div>{tag}</div>}
          <div className="flex flex-col gap-1">
            <span className="text-text-primary font-medium">{title}</span>
            {description && (
              <span className="text-text-secondary text-sm">{description}</span>
            )}
          </div>
        </div>

        <span
          className={twMerge(
            `border-border grid size-5 shrink-0 place-items-center rounded-full border-2 transition-colors`,
            isSelected && "border-primary/50",
          )}
          aria-hidden
        >
          {isSelected && <span className="bg-primary size-3 rounded-full" />}
        </span>
      </label>

      {isSelected && children && (
        <div className="px-4 pb-4">
          <Divider className="mb-3" />
          {children}
        </div>
      )}
    </div>
  );
}
